import { Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';
import * as React from 'react';

import { DataAPIContext } from '../api';
import { GET_ONE, CREATE } from '../api/dataProvider';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import { actionName, capitalize, SCOPE } from './agroups/utils';
import { useSnackbar } from 'notistack';

export const UserProfilePermissions = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { hasPerm, user } = React.useContext(ContextStore);
    const [perms, setPerms] = React.useState(null);
    const [tagmap, setTagmap] = React.useState(null);
    const [ags, setAgs] = React.useState(null);
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar } = useSnackbar();

    // CP-8204: refreshing your OWN cache is always allowed server-side; refreshing
    // someone else's requires user.write over them. Tag-scope mismatches on specific
    // targets are reported by the backend — here we only disable the clearly-hopeless
    // case (no user.write at all, viewing another user's profile).
    const isSelf = user && String(user.id) === String(props.id);
    const canRefresh = isSelf || hasPerm('user', 'write');


    const parsedPerms = {};
    perms && perms.perms &&
        Object.keys(perms.perms).forEach((scope) => {
            console.log('node scope', scope, perms.perms[scope]);
            const d = {};
            Object.keys(perms.perms[scope]).forEach((action) => {
                if (perms.full_access || (perms.scoped_all_tags && perms.scoped_all_tags[scope] && perms.scoped_all_tags[scope][action])) {
                    d[action] = () => Object.keys(tagmap).concat(null);
                } else {
                    d[action] = () => perms.perms[scope][action];
                }
            });
            parsedPerms[scope] = d;
        });
    perms && perms.scoped_all_tags &&
        Object.keys(perms.scoped_all_tags).forEach((scope) => {
            console.log('node scoped_all_tags', scope, perms.scoped_all_tags[scope]);
            if (!parsedPerms[scope]) {
                parsedPerms[scope] = {};
            }
            Object.keys(perms.scoped_all_tags[scope]).forEach((action) => {
                parsedPerms[scope][action] = () => Object.keys(tagmap).concat(null);
            });
        });

    React.useEffect(() => {
        if (perms) {
            return;
        }
        dataAPI(GET_ONE, 'user', {
            id: props.id,
            urlSuffix: 'permissions/',
        }).then((res) => {
            if (res.statusCode === 200) {
                console.log('node got response', res.data);
                setTagmap(res.data.tagmap);
                setPerms(res.data.cache);
                setAgs(res.data.agroups);
            }
        });
    }, [props.id, dataAPI, perms]);

    const onRefreshPermissionCache = (ev) => {
        dataAPI(CREATE, 'user', {
                id: props.id,
                urlSuffix: `${props.id}/refresh_permission_cache/`
            }).then((res) => {
                if (res.statusCode === 200) {
                    setPerms(res.data.cache);
                    enqueueSnackbar('Permission cache refreshed', { variant: 'success' });
                } else {
                    enqueueSnackbar(res.data?.message || 'Failed to refresh permission cache', { variant: 'error' });
                }
            });
    }


    const mainContainerStyles = isSmallScreen ? { padding: '2rem', width: '80%', maxHeight: 'calc(100vh - 18rem)', overflowY: "auto", overflowX: 'hidden' } : { padding: '1rem 2rem' }

    return (
        <div style={mainContainerStyles}>
            <Stack spacing={3} sx={{ width: '100%' }}>
                <Typography variant="h6">User access groups</Typography>
                <Box component="div">
                    {ags?.map((ag, idx, lst) => (
                        <Box key={ag.id} component="span">
                            <Link
                                href={`/user/permissions/group/${ag.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'inline-block', lineHeight: 1.5 }}
                            >
                                {ag.name}
                            </Link>
                            {idx < lst.length - 1 && ', '}
                        </Box>
                    ))}
                </Box>
                <Typography variant="h6">Cached user permissions</Typography>
                {Object.keys(parsedPerms).map((scope) => (
                    <Stack key={scope} spacing={2} sx={{ width: '100%' }}>
                        <Typography sx={{ fontWeight: 500 }}>{SCOPE(scope)?.name || capitalize(scope)}</Typography>
                        {Object.keys(parsedPerms[scope]).map((action) => (
                            <Box
                                key={action}
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    gap: { xs: 0.5, md: 2 },
                                    alignItems: { xs: 'flex-start', md: 'flex-start' },
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: { xs: '100%', md: 'auto' },
                                        minWidth: { md: '7.5rem' },
                                        textAlign: { xs: 'left', md: 'right' },
                                    }}
                                >
                                    {actionName(scope, action) || capitalize(action)}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {parsedPerms[scope][action]().map((tagid, idx, lst) => (
                                        <Box key={tagid} component="span">
                                            {!!tagid ? (
                                                <Link
                                                    href={`/user/permissions/tag/${tagid}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ display: 'inline-block', lineHeight: 1.5 }}
                                                >
                                                    {(tagmap && tagmap[tagid]?.name) || tagid}
                                                </Link>
                                            ) : (
                                                'All untagged resources [*]'
                                            )}
                                            {idx < lst.length - 1 && ', '}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                ))}
                <Tooltip title={canRefresh ? '' : "Requires the User write permission (you can always refresh your own cache from your profile)"}>
                    <span>
                        <Button onClick={onRefreshPermissionCache} disabled={!canRefresh} variant="outlined" color="primary">Refresh user's permission cache</Button>
                    </span>
                </Tooltip>
            </Stack>
        </div>
    );
};

export default UserProfilePermissions;
